import { Source } from '@chegg-wt/core/types/core'
import { Config } from '../config'
import { JOURNAL, JSTOR } from '../constants/journalTypes'
import { SourceEnum } from '../globalTypes'
import {
  AutociteCslData,
  AutociteCslData_autociteCslData,
  AutociteCslData_autociteCslData_authors,
} from '../oneGraphQueries/__generated__/AutociteCslData'
import {
  citationSearchWidgetRegion,
  emptyCitationSearchResultRegion,
  ComponentRegion,
  componentOrigins,
} from '../state/instrumentation/rio/clickstreamComponentViews'
import { cslToRioAutociteResponse, numberOfAutociteFields } from './plusCiteRioEvent'
import { cslV1ToRioAutociteResponse } from './autociteRioEvents'

export const months = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

export const formattedAutociteSource = (source: string) =>
  source === 'webpage' ? 'website' : source

const formatContribs = (type: string, cslData) =>
  cslData[type]
    ? cslData[type].map((contrib) => ({
        last: contrib.family,
        first: contrib.given,
        function: type,
      }))
    : []

const formattedContribs = (cslData) =>
  formatContribs('author', cslData).concat(
    formatContribs('editor', cslData),
    formatContribs('translator', cslData)
  )

export const cslToEB = (cslObj: { results: Object }): Source[] => {
  if (!Array.isArray(cslObj.results) || cslObj.results.length === 0) return []
  const cslData = cslObj.results[0].csl
  const dateAccessed = cslData.accessed['date-parts'][0]
  const dateIssued = cslData.issued['date-parts'][0]
  return [
    {
      data: {
        pubonline: {
          yearaccessed: dateAccessed[0] || '',
          monthaccessed: dateAccessed[1] ? months[dateAccessed[1] - 1] : '',
          dayaccessed: dateAccessed[2] || '',
          url: cslData.URL,
          title: cslData['container-title'],
          inst: cslData.publisher,
          day: dateIssued[2] || '',
          month: dateIssued[1] ? months[dateIssued[1] - 1] : '',
          year: dateIssued[0] || '',
        },
        [formattedAutociteSource(cslData.type)]: {
          title: cslData.title,
        },
        contributors: formattedContribs(cslData),
        source: cslData.type,
        autocite: {
          url: cslData.URL,
        },
      },
      display: {
        page_title: cslData.title,
      },
      feedback: cslObj.results[0].feedback,
      autociteResponseForRio: cslToRioAutociteResponse(cslObj.results[0]),
      numFieldsAutocite: numberOfAutociteFields(cslData),
    },
  ]
}

const isUrl = (term) => /^(http:\/\/|https:\/\/|www\.)/.test(term)

export const isAutociteUrl = (sourceType: string, query: string): boolean => {
  return ['film', 'website'].includes(sourceType) && isUrl(query)
}

export const isWrongTypeError = (error: Error) =>
  error.message.includes('This URL is a PDF, image or Office Document')

export const getSourceType = (source: Source): string => source.data.source

export const getAutociteUrl = (source: Source): string => {
  const { autocite } = source.data
  return autocite?.search || autocite?.url || ''
}

export const hasAutociteUrl = (source: Source): boolean => {
  const sourceType = getSourceType(source)
  const autociteUrl = getAutociteUrl(source)
  return isAutociteUrl(sourceType, autociteUrl)
}

export enum SourceType {
  website = 'website',
  article = 'article',
  journal = 'journal',
  book = 'book',
}

export const sourceSearchPlaceholder = {
  book: 'Search by title or ISBN',
  website: 'Search by keyword or URL',
  journal: 'Search by title or DOI',
  default: 'Search by keyword',
}

export const worldCatSourceTypes = [
  'bible',
  'book',
  'chapter',
  'conference',
  'congress',
  'dictionary',
  'dissertation',
  'dissertation-abstract',
  'encyclopedia',
  'federal-bill',
  'film',
  'govt',
  'introduction',
  'map',
  'multivolume',
  'musical-recording',
  'software',
  'thesis',
]

export const evalSourceTypes = ['website', 'book', 'journal']

export const hasWorldcatLink = (type: string) => worldCatSourceTypes.includes(type)

export const addWorldcatLinkMaybe = (sourceType: string, sources: Source[]): Source[] =>
  hasWorldcatLink(sourceType)
    ? sources.map((source) => {
        const { oclc } = source.data.other
        const link = `${Config.worldcatUrl}/${oclc}`
        return { link, ...source }
      })
    : sources

export const journalTypeSwitch = {
  [JOURNAL]: JSTOR,
  [JSTOR]: JOURNAL,
}
export const journalTypeSwitchText = {
  [JOURNAL]: 'JSTOR',
  [JSTOR]: 'Crossref',
}

export const SEARCH_BOTTOM = 'searchBottom'
export const SEARCH_RESULT = 'searchResult'

export type TooltipLocation = typeof SEARCH_BOTTOM | typeof SEARCH_RESULT

export const tooltipRioRegionMap: { [key in TooltipLocation]: ComponentRegion } = {
  [SEARCH_BOTTOM]: citationSearchWidgetRegion,
  [SEARCH_RESULT]: emptyCitationSearchResultRegion,
}

export const sourceRioOriginMap = {
  [SourceEnum.journal]: componentOrigins.doiLink,
  [SourceEnum.book]: componentOrigins.isbnLink,
}

// Transformation functions for new autocite API

const formatContribsV1 = (
  type: string,
  contributors: AutociteCslData_autociteCslData_authors[]
) =>
  contributors
    ? contributors.map((contrib) => ({
        last: contrib.lastName,
        first: contrib.givenName,
        function: type,
      }))
    : []

const formattedContribsV1 = (csl: AutociteCslData_autociteCslData) => {
  return formatContribsV1('author', csl.authors).concat(
    formatContribsV1('editor', csl.editors),
    formatContribsV1('translator', csl.translators)
  )
}

export const cslOneGraphToEb = (data: AutociteCslData): Source[] => {
  if (!data || !data.autociteCslData) return []
  const csl = data.autociteCslData
  return [
    {
      data: {
        pubonline: {
          yearaccessed: csl.accessedPublication?.year?.toString() || '',
          monthaccessed: csl.accessedPublication?.month || '',
          dayaccessed: csl.accessedPublication?.day?.toString() || '',
          url: csl.url,
          title: csl.containerTitle,
          inst: csl.publisherName,
          day: csl.issuedPublication?.day?.toString() || '',
          month: csl.issuedPublication?.month || '',
          year: csl.issuedPublication?.year?.toString() || '',
        },
        [formattedAutociteSource(csl.contentType)]: {
          title: csl.contentTitle,
        },
        contributors: formattedContribsV1(csl),
        source: csl.contentType,
        autocite: {
          url: csl.url,
        },
      },
      display: {
        page_title: csl.contentTitle,
      },
      autociteResponseForRio: cslV1ToRioAutociteResponse(csl),
    },
  ]
}
